import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import axios from 'axios';
import { Calendar, User, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sample blog posts for demo
const SAMPLE_POSTS = [
  {
    id: '1',
    title: 'Los 10 destinos más increíbles para 2025',
    slug: 'destinos-increibles-2025',
    excerpt: 'Descubre los lugares que deberías visitar este año. Desde playas paradisíacas hasta ciudades con historia milenaria.',
    content: '',
    image_url: 'https://images.unsplash.com/photo-1488085061387-422e29b40080?auto=format&fit=crop&q=80&w=800',
    author: 'Equipo Viajes de Ensueño',
    created_at: '2025-01-15'
  },
  {
    id: '2',
    title: 'Guía completa para viajar con niños',
    slug: 'viajar-con-ninos',
    excerpt: 'Consejos prácticos para que viajar en familia sea una experiencia inolvidable y sin estrés.',
    content: '',
    image_url: 'https://images.unsplash.com/photo-1471899236350-e3016bf1e69e?auto=format&fit=crop&q=80&w=800',
    author: 'María García',
    created_at: '2025-01-10'
  },
  {
    id: '3',
    title: 'Luna de miel: destinos románticos únicos',
    slug: 'luna-de-miel-romantica',
    excerpt: 'Ideas inspiradoras para el viaje más especial de vuestra vida. Desde Maldivas hasta la Toscana italiana.',
    content: '',
    image_url: 'https://images.unsplash.com/photo-1544078751-58fee2d8a03b?auto=format&fit=crop&q=80&w=800',
    author: 'Carlos Rodríguez',
    created_at: '2025-01-05'
  }
];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Blog de Viajes | Inspiración para tus Viajes de Ensueño";
    const description = "Descubre consejos de viaje, guías de destinos y las mejores experiencias de nuestra mano. Tu blog de referencia para planificar tu próxima aventura.";
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await axios.get(`${API}/blog`);
        setPosts(res.data.length > 0 ? res.data : SAMPLE_POSTS);
      } catch (err) {
        setPosts(SAMPLE_POSTS);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "d 'de' MMMM, yyyy", { locale: es });
    } catch {
      return dateStr;
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <div className="relative h-[40vh] min-h-[300px]">
        <img
          src="https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&q=80&w=2000"
          alt="Blog"
          className="w-full h-full object-cover"
        />
        <div className="hero-gradient absolute inset-0" />
        
        <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16">
          <div className="container-custom">
            <h1 className="text-4xl lg:text-6xl font-serif text-white mb-4" data-testid="blog-title">
              Blog de Viajes
            </h1>
            <p className="text-xl text-white/80 max-w-2xl">
              Inspiración, consejos y guías para tu próxima aventura
            </p>
          </div>
        </div>
      </div>

      {/* Posts Grid */}
      <section className="section">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="skeleton h-96 rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <article
                  key={post.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 group"
                  data-testid={`blog-post-${index}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/800x450?text=Imagen+no+disponible';
                      }}
                    />
                  </div>
                  
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(post.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {post.author}
                      </span>
                    </div>

                    <h2 className="text-xl font-serif font-semibold text-primary mb-3 group-hover:text-secondary transition-colors">
                      {post.title}
                    </h2>

                    <p className="text-muted-foreground mb-4 line-clamp-2">
                      {post.excerpt.replace(/<[^>]*>/g, '')}
                    </p>

                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-2 text-primary font-medium hover:text-accent transition-colors"
                    >
                      Leer más
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}

          {posts.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                Próximamente nuevos artículos. ¡Mantente atento!
              </p>
            </div>
          )}
        </div>
      </section>

      <div className="h-32 bg-slate-50" />
    </Layout>
  );
}
